'use client';

import React from 'react';
import { styled } from '@linaria/react';
import { motion } from 'framer-motion';
import { RefreshCw } from 'lucide-react';

import DefaultButton from '@/components/DefaultButton';

import Snapshot from './Snapshot';

const INITIAL_TRANSFORMS = [
  'translate(-25%, -30%) rotate(-16deg)',
  'translate(25%, -8%) rotate(12deg)',
  'translate(4%, 20%) rotate(-3deg)',
];

interface SnapshotData {
  num: number;
  state: string;
  code: string;
  snippet: React.ReactElement;
}

interface Props {
  data: Array<SnapshotData>;
}

function DraggableSnapshots({ data }: Props) {
  const dragParentRef = React.useRef<HTMLElement>(null);

  const [cardsKey, setCardsKey] = React.useState<number | 'initial'>(
    'initial'
  );

  return (
    <Wrapper>
      <DragArea ref={dragParentRef} key={cardsKey}>
        {data.map((snap, index) => (
          <SnapWrapper
            key={index}
            drag
            dragConstraints={dragParentRef}
            dragSnapToOrigin={false}
          >
            <Snapshot
              key={snap.num}
              {...snap}
              transform={INITIAL_TRANSFORMS[index]}
            />
          </SnapWrapper>
        ))}
      </DragArea>
      <ResetRow>
        <Button onClick={() => setCardsKey(Math.random())}>
          <RefreshCw size={16} /> Reset Cards
        </Button>
      </ResetRow>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  margin-top: 100px;
  margin-bottom: 48px;
  animation: fadeIn 400ms;
`;

const DragArea = styled.section`
  display: grid;
  justify-content: center;
  align-items: center;
  height: 30rem;
`;

const SnapWrapper = styled(motion.div)`
  grid-row: 1;
  grid-column: 1;
  cursor: grab;
  will-change: transform;
  pointer-events: none;

  &:active {
    cursor: grabbing;
  }
`;

const ResetRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px;
  border: 1px dashed var(--color-gray-300);
  border-radius: 8px;
`;

const Button = styled(DefaultButton)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 24px;

  html[data-color-mode='dark'] & {
    color-scheme: dark;
  }
`;

export default DraggableSnapshots;
